import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Offcanvas } from "react-bootstrap";
import { useGetProductListQuery } from "../../utils/api/product/product.api";
import { getImage } from "../../utils/Utils";
import Empty from "../../assets/images/appImages/category.png";
import type { Category } from "../../utils/api/category/category.type";

export interface BrandMobileModalProps {
  show: boolean;
  setShow: (v: boolean) => void;
  selectedCategory: Category | null;
  renderProductItem?: (item: any) => React.ReactNode;
}

const BrandMobileModal = ({
  show,
  setShow,
  selectedCategory,
  renderProductItem,
}: BrandMobileModalProps) => {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<"subcategories" | "products">(
    "subcategories"
  );
  const [selectedSubCategory, setSelectedSubCategory] = useState<{
    id: string;
    slug: string;
  } | null>(null);

  const handleClose = () => {
    setShow(false);
    setViewMode("subcategories");
    setSelectedSubCategory(null);
  };

  const shouldShowSubcategories =
    selectedCategory?.sous_categorie &&
    selectedCategory.sous_categorie.length > 0;
  const currentView = shouldShowSubcategories ? viewMode : "products";

  const { data: productsData = { results: [], count: 0 }, isFetching } =
    useGetProductListQuery(
      {
        page: 1,
        limit: 20,
        categorie: selectedCategory?.slug || "",
        sous_categorie: selectedSubCategory?.slug ?? undefined,
      },
      { skip: !selectedCategory || !show }
    );

  const handleSubCategoryClick = (subCategory: { id: string; slug: string }) => {
    setSelectedSubCategory(subCategory);
    setViewMode("products");
  };

  const handleBackToSubcategories = () => {
    setViewMode("subcategories");
    setSelectedSubCategory(null);
  };

  const handleProductClick = (slug: string) => {
    navigate(`/produit/${slug}`);
    handleClose();
  };

  const title =
    currentView === "subcategories" && shouldShowSubcategories
      ? selectedCategory?.nom
      : selectedSubCategory
        ? selectedCategory?.sous_categorie?.find(
            (s) => s.slug === selectedSubCategory.slug
          )?.nom ?? selectedCategory?.nom
        : selectedCategory?.nom;

  const handleHeaderBack = () => {
    if (currentView === "products" && shouldShowSubcategories) {
      handleBackToSubcategories();
      return;
    }
    handleClose();
  };

  if (!selectedCategory) return null;

  return (
    <Offcanvas
      show={show}
      onHide={handleClose}
      placement="end"
      className="brand-mobile-modal-offcanvas"
    >
      <Offcanvas.Header>
        <button
          type="button"
          className="btn btn-link p-0 brand-mobile-modal-back"
          onClick={handleHeaderBack}
          aria-label="Retour"
        >
          ←
        </button>
        <Offcanvas.Title>{title}</Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body>
        {currentView === "subcategories" && shouldShowSubcategories && (
          <ul className="brand-mobile-subcategory-list">
            {selectedCategory.sous_categorie.map((subCategory) => (
              <li key={subCategory.id} className="brand-mobile-subcategory-list-item">
                <button
                  type="button"
                  className="brand-mobile-subcategory-list-btn"
                  onClick={() => handleSubCategoryClick(subCategory)}
                >
                  <span className="brand-mobile-subcategory-list-name">
                    {subCategory.nom}
                  </span>
                  <span className="brand-mobile-subcategory-list-arrow" aria-hidden="true">
                    →
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}

        {currentView === "products" && (
          <>
            {shouldShowSubcategories && (
              <button
                type="button"
                className="btn btn-link p-0 mb-3 brand-mobile-modal-back"
                onClick={handleBackToSubcategories}
              >
                ← Retour aux sous-catégories
              </button>
            )}
            {isFetching ? (
              <div className="text-center py-4">Chargement...</div>
            ) : productsData.results?.length === 0 ? (
              <div className="text-center py-4 text-muted">
                Aucun produit dans cette catégorie.
              </div>
            ) : (
              <div className="best-sellers-section-grid">
                {productsData.results?.map((product: any, key: number) => (
                  <div
                    key={product?.id ?? key}
                    className="best-sellers-section-grid-item"
                    onClick={handleClose}
                    onKeyDown={(e) =>
                      e.key === "Enter" && handleClose()
                    }
                    role="presentation"
                  >
                    {renderProductItem ? (
                      renderProductItem(product)
                    ) : (
                      <NavLink
                        to={`/produit/${product?.slug}`}
                        state={product}
                        className="no-link w-100"
                        onClick={(e) => {
                          e.preventDefault();
                          handleProductClick(product?.slug);
                        }}
                      >
                        <div className="product-item-container">
                          <div className="content-img-product-item">
                            <img
                              loading="lazy"
                              src={
                                product?.images?.[0]?.image
                                  ? getImage(product.images[0].image)
                                  : Empty
                              }
                              alt={product?.nom}
                              className="img-product-item"
                            />
                          </div>
                          <div className="container-body-product-item">
                            <p className="name-product-item my-2">
                              {product?.nom}
                            </p>
                          </div>
                        </div>
                      </NavLink>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </Offcanvas.Body>
    </Offcanvas>
  );
};

export default BrandMobileModal;
